import type { ReactNode } from "react";

export type IdentityVerificationTone =
  | "info"
  | "success"
  | "warning"
  | "error";

export interface IdentityVerificationBannerProps {
  tone?: IdentityVerificationTone;
  title?: string;
  message: string;
  action?: ReactNode;
}

export function IdentityVerificationBanner({
  tone = "info",
  title,
  message,
  action,
}: IdentityVerificationBannerProps) {
  const borderColor = toneColor(tone);
  const bg = toneBg(tone);
  const fg = toneText(tone);
  const role = tone === "error" ? "alert" : "status";
  return (
    <div
      className="sc-identity-verify-banner"
      role={role}
      aria-live={tone === "error" ? "assertive" : "polite"}
      style={{
        borderLeft: `4px solid ${borderColor}`,
        background: bg,
        color: fg,
        borderRadius: "var(--sc-identity-radius)",
      }}
    >
      <div className="sc-identity-verify-banner__body">
        {title ? (
          <div className="sc-identity-verify-banner__title" style={{ fontWeight: 700 }}>
            {title}
          </div>
        ) : null}
        <div className="sc-identity-verify-banner__message">{message}</div>
        {action ? (
          <div className="sc-identity-verify-banner__action">{action}</div>
        ) : null}
      </div>
    </div>
  );
}

function toneColor(tone: IdentityVerificationTone): string {
  switch (tone) {
    case "success":
      return "var(--sc-identity-success)";
    case "warning":
      return "#d97706";
    case "error":
      return "var(--sc-identity-error)";
    case "info":
    default:
      return "var(--sc-identity-primary)";
  }
}

function toneBg(tone: IdentityVerificationTone): string {
  switch (tone) {
    case "success":
      return "#ecfdf2";
    case "warning":
      return "#fff8eb";
    case "error":
      return "#fef2f2";
    case "info":
    default:
      return "#eff4ff";
  }
}

function toneText(tone: IdentityVerificationTone): string {
  switch (tone) {
    case "success":
      return "#14532d";
    case "warning":
      return "#78350f";
    case "error":
      return "#7f1d1d";
    case "info":
    default:
      return "#1e3a8a";
  }
}
